import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { FilmSlate, GearSix, CaretLeft, CaretRight, Sparkle } from "@phosphor-icons/react";
import CreativeDirectorCard from "./CreativeDirectorCard";
import CreativeDirectorBrowseModal from "./CreativeDirectorBrowseModal";
import GlassButton from "./GlassButton";

/**
 * <CreativeDirectorPicker/> — premium image-led CDS discovery for AI Pro Studio.
 *
 * Public contract preserved (do not break `AiProStudio.jsx`):
 *   props: { selected, onSelect, isAdmin, onManage, refreshKey }
 *   behavior: `onSelect(director)` on positive click, `onSelect(null)` on
 *             re-click (deselect) — matches the previous flat-button UX.
 *
 * Layout (from top → bottom):
 *   1. Header (title + Manage/Browse-All controls)
 *   2. Fresh Drops row (only visible when at least one record has is_fresh_drop=true)
 *   3. Quick Start carousel (uses is_quickstart records; falls back to first 12
 *      by sort_order — as a *display-only* fallback, never writes to the DB)
 *   4. Selected chip (shown when a CDS is picked)
 *
 * The endpoint /api/creative-directors already filters `library_only!=true` and
 * strips `hidden_prompt` server-side, so those visibility rules are honored
 * everywhere below by construction.
 */

const QUICK_START_FALLBACK_LIMIT = 12;

// Fresh Drop must be flagged AND either have no expiration or expire in the future.
function isActiveFreshDrop(d) {
  if (!d?.is_fresh_drop) return false;
  const until = d.featured_until;
  if (!until) return true;
  const t = Date.parse(until);
  if (Number.isNaN(t)) return true;   // malformed date: treat as no expiration
  return t > Date.now();
}

// Ordering for a specific section. Section-specific sort_order takes priority
// when present; falls back to the record's global sort_order.
function orderBy(section) {
  const field = {
    quickstart: "quickstart_sort_order",
    fresh: "fresh_drop_sort_order",
    featured: "featured_sort_order",
  }[section];
  return (a, b) => {
    const av = a?.[field] ?? a?.sort_order ?? 999;
    const bv = b?.[field] ?? b?.sort_order ?? 999;
    if (av !== bv) return av - bv;
    return (a?.name || "").localeCompare(b?.name || "");
  };
}

/** Horizontal carousel with left/right arrows + snap scrolling. */
function CardRail({ items, selected, onSelect, testid, dataEmpty = "cds-rail-empty" }) {
  const scrollerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const [page, setPage] = useState({ cur: 1, total: 1 });

  const recomputeBounds = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    // Rough page estimate — each card ≈ 200px including gap
    const perPage = Math.max(1, Math.round(el.clientWidth / 200));
    const total = Math.max(1, Math.ceil(items.length / perPage));
    const cur = Math.min(total, Math.floor(el.scrollLeft / el.clientWidth) + 1);
    setPage({ cur, total });
  };

  useEffect(() => {
    recomputeBounds();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", recomputeBounds, { passive: true });
    window.addEventListener("resize", recomputeBounds);
    return () => {
      el.removeEventListener("scroll", recomputeBounds);
      window.removeEventListener("resize", recomputeBounds);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const nudge = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.85), behavior: "smooth" });
  };

  if (!items.length) {
    return <p data-testid={dataEmpty} className="text-xs text-zinc-600 py-2">Nothing here yet.</p>;
  }

  return (
    <div className="relative" data-testid={testid}>
      {/* Left arrow — hidden on mobile (users swipe) */}
      <div className="hidden sm:flex absolute left-[-6px] top-1/2 -translate-y-1/2 z-10">
        <GlassButton
          data-testid={`${testid}-left`}
          tone="chrome"
          size="sm"
          disabled={!canLeft}
          onClick={() => nudge(-1)}
          aria-label="Scroll left"
          className="!h-9 !w-9 !p-0 !rounded-full"
        >
          <CaretLeft size={14} weight="bold" />
        </GlassButton>
      </div>

      <div
        ref={scrollerRef}
        // Vertical padding gives the selected gold halo + hover platinum
        // glow ~14px of vertical breathing room before overflow-y clipping
        // engages. Horizontal remains overflow-x-auto for swipe/scroll.
        className="flex items-stretch gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar px-2 py-4 sm:px-8"
        style={{ scrollbarWidth: "none" }}
      >
        {items.map((d) => (
          <div key={d.id} className="snap-start shrink-0 w-44">
            <CreativeDirectorCard
              director={d}
              selected={selected?.id === d.id}
              onSelect={onSelect}
            />
          </div>
        ))}
      </div>

      <div className="hidden sm:flex absolute right-[-6px] top-1/2 -translate-y-1/2 z-10">
        <GlassButton
          data-testid={`${testid}-right`}
          tone="chrome"
          size="sm"
          disabled={!canRight}
          onClick={() => nudge(1)}
          aria-label="Scroll right"
          className="!h-9 !w-9 !p-0 !rounded-full"
        >
          <CaretRight size={14} weight="bold" />
        </GlassButton>
      </div>

      {page.total > 1 && (
        <div className="mt-2 text-[10px] text-zinc-500 text-center tracking-wider" data-testid={`${testid}-page`}>
          {page.cur} / {page.total}
        </div>
      )}
    </div>
  );
}

export const CreativeDirectorPicker = ({ selected, onSelect, isAdmin, onManage, refreshKey }) => {
  const [directors, setDirectors] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [browseOpen, setBrowseOpen] = useState(false);
  const browseTriggerRef = useRef(null);

  // Preload both frame WebPs once per picker mount. Every card references
  // the same two URLs; browser cache serves subsequent requests instantly.
  useEffect(() => {
    const a = new Image();
    a.decoding = "async";
    a.src = "/cds/fs_btn1_1080.webp";
    const b = new Image();
    b.decoding = "async";
    b.src = "/cds/fs_btn2_1080.webp";
  }, []);

  useEffect(() => {
    api.get("/creative-directors")
      .then((r) => setDirectors(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [refreshKey]);

  // ------- Section computations (memoized) -------
  const freshDrops = useMemo(
    () => directors.filter(isActiveFreshDrop).sort(orderBy("fresh")).slice(0, 12),
    [directors],
  );

  const quickStart = useMemo(() => {
    // Explicit admin picks take priority — as soon as one CDS has
    // is_quickstart=true, the fallback list is discarded.
    const explicit = directors.filter((d) => d.is_quickstart).sort(orderBy("quickstart"));
    if (explicit.length > 0) return explicit.slice(0, QUICK_START_FALLBACK_LIMIT);
    // Display-only fallback — NEVER mutates the DB, NEVER sets is_quickstart.
    return [...directors]
      .sort(orderBy("quickstart"))
      .slice(0, QUICK_START_FALLBACK_LIMIT);
  }, [directors]);

  // ------- Empty states -------
  if (loaded && directors.length === 0 && !isAdmin) return null;

  return (
    <div
      data-testid="director-picker"
      className="rounded-2xl bg-[linear-gradient(180deg,#030303_0%,#000000_100%)] p-5 sm:p-6"
      style={{
        // Matching card treatment: bright upper edge, chrome ring, dark
        // bottom — makes the whole panel feel like a single milled shell
        // that the cards sit within.
        boxShadow: [
          "inset 0 1.5px 0 rgba(255,255,255,0.16)",
          "inset 0 0 0 1px rgba(255,255,255,0.06)",
          "inset 0 -1.5px 0 rgba(0,0,0,0.55)",
          "0 20px 60px -30px rgba(0,0,0,0.9)",
        ].join(", "),
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <FilmSlate size={18} weight="fill" className="text-amber-300" />
        <p className="font-display text-lg font-bold tracking-tight">Choose a Creative Director</p>
        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/5 text-zinc-400 ring-1 ring-white/10">
          Optional
        </span>
        <div className="ml-auto flex items-center gap-2">
          <GlassButton
            ref={browseTriggerRef}
            data-testid="cds-browse-all-btn"
            tone="chrome"
            size="sm"
            onClick={() => setBrowseOpen(true)}
          >
            <Sparkle size={12} weight="fill" /> Browse All Styles
          </GlassButton>
          {isAdmin && (
            <GlassButton
              data-testid="director-manage-btn"
              tone="chrome"
              size="sm"
              onClick={onManage}
            >
              <GearSix size={12} weight="bold" /> Manage
            </GlassButton>
          )}
        </div>
      </div>
      <p className="text-[11px] text-zinc-500 mb-5">
        Each director adds a professional hidden art direction to your next generation. Click again to deselect.
      </p>

      {/* Empty state (admin only) */}
      {loaded && directors.length === 0 && isAdmin && (
        <p data-testid="director-picker-empty" className="text-xs text-zinc-600 mb-4">
          No Creative Directors published yet — click Manage to create your first preset.
        </p>
      )}

      {/* Fresh Drops row (only rendered when it has content) */}
      {freshDrops.length > 0 && (
        <div className="mb-6">
          <div className="flex items-baseline gap-2 mb-2 px-1">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-300">Fresh Drops</p>
            <span className="text-[10px] text-zinc-600">newest releases</span>
          </div>
          <CardRail
            items={freshDrops}
            selected={selected}
            onSelect={onSelect}
            testid="cds-fresh-rail"
          />
        </div>
      )}

      {/* Quick Start */}
      {quickStart.length > 0 && (
        <div>
          <div className="flex items-baseline gap-2 mb-2 px-1">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-300">Quick Start</p>
            <span className="text-[10px] text-zinc-600">popular picks · start here</span>
          </div>
          <CardRail
            items={quickStart}
            selected={selected}
            onSelect={onSelect}
            testid="cds-quickstart-rail"
          />
        </div>
      )}

      {/* Currently selected chip — sanity check for the user */}
      {selected && (
        <div className="mt-5 flex items-center gap-2" data-testid="cds-selected-chip">
          <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Selected</span>
          <span className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-amber-400/10 text-amber-200 ring-1 ring-amber-400/40 text-xs font-semibold">
            {selected.name}
          </span>
          <button
            data-testid="cds-clear-selected"
            onClick={() => onSelect?.(null)}
            className="text-[10px] text-zinc-500 hover:text-white transition-colors"
          >
            Clear
          </button>
        </div>
      )}

      <CreativeDirectorBrowseModal
        open={browseOpen}
        onClose={() => setBrowseOpen(false)}
        directors={directors}
        selected={selected}
        onSelect={onSelect}
      />
    </div>
  );
};
